import R from 'ramda';
import moment from 'moment';
import { createSelector } from 'reselect';

import { groupRoomsByCoords } from 'ha/utils/groupRoomsByCoords';
import formatDate from 'ha/utils/formatDate';
import { getMediaQueryBreakpoints } from 'ha/modules/UI/selectors';
import { TypeValue } from 'ha/types/SearchFilters';
import { getUserId } from 'ha/modules/AuthLogic/selectors';
import getPageInfo from './getPageInfo';
import getImagesSize from './getPreviewImagesSize';
import { getKindsFromCategories, areSearchAlertsEqual } from '../utils';
import {
  getFurnitureFilter as getFurniture,
  getBillsFilter as getBills,
  getRoomsFilter as getRooms,
  getCategoriesFilter as getCategories,
  getCollectionFilter as getCollection,
  getRegistrationFilter as getRegistration,
  getRulesFilter as getRules,
  getSuitableForFilter as getSuitableFor,
  getRecentlyAddedFilter as getRecentlyAdded,
  getGenderFilter as getGender,
  getPreviewNumberOfListings as getPreviewNumberOfListingsSelector,
  getPreviewRequestId as getPreviewRequestIdSelector,
  getIsLoadingSearchAlert as isLoadingSearchAlert,
  getIsErrorSearchAlert as isErrorSearchAlert,
  getCurrentSearchAlert as currentSearchAlert,
  isSuggestionBarVisible as isSuggestionBarVisibleSelector,
} from '../reducer';
import {
  STATE_NAME,
  PriceDistributionSteps,
  RequiredAnalyticsFilters,
} from '../constants';

export { default as getCountryCode } from './getCountryCode';
export { getPageInfo };
export * from './getRequestCache';

const getSearch = s => s[STATE_NAME];

export const getFurnitureFilter = createSelector(
  getSearch,
  getFurniture,
);
export const getBillsFilter = createSelector(
  getSearch,
  getBills,
);
export const getRoomsFilter = createSelector(
  getSearch,
  getRooms,
);
export const getCategoriesFilter = createSelector(
  getSearch,
  getCategories,
);
export const getCollectionFilter = createSelector(
  getSearch,
  getCollection,
);
export const getRegistrationFilter = createSelector(
  getSearch,
  getRegistration,
);
export const getRulesFilter = createSelector(
  getSearch,
  getRules,
);
export const getSuitableForFilter = createSelector(
  getSearch,
  getSuitableFor,
);
export const getRecentlyAddedFilter = createSelector(
  getSearch,
  getRecentlyAdded,
);
export const getGenderFilter = createSelector(
  getSearch,
  getGender,
);
export const getPreviewNumberOfListings = createSelector(
  getSearch,
  getPreviewNumberOfListingsSelector,
);
export const getPreviewRequestId = createSelector(
  getSearch,
  getPreviewRequestIdSelector,
);
export const getIsLoadingSearchAlert = createSelector(
  getSearch,
  isLoadingSearchAlert,
);
export const getIsErrorSearchAlert = createSelector(
  getSearch,
  isErrorSearchAlert,
);
export const getCurrentSearchAlert = createSelector(
  getSearch,
  currentSearchAlert,
);
export const getlangURLListState = createSelector(
  getSearch,
  search => search.langURLList,
);
export const getCitiesNearbyState = createSelector(
  getSearch,
  search => search.citiesNearby,
);
export const getDateFilter = createSelector(
  getSearch,
  search => search.dateFilter,
);
export const getListings = createSelector(
  getSearch,
  search => search.listings,
);
export const getIsSearchEmpty = createSelector(
  getListings,
  listings => listings.length === 0,
);
export const getMap = createSelector(
  getSearch,
  search => search.map,
);
export const getCityName = createSelector(
  getMap,
  map => map.cityLocalized,
);
export const getCountryName = createSelector(
  getMap,
  map => map.countryLocalized,
);
export const getPriceFilter = createSelector(
  getSearch,
  search => search.priceFilter,
);
export const getCurrencyRates = createSelector(
  getSearch,
  search => search.currencyRates,
);
export const getIsFilterPopoverOpen = createSelector(
  getPageInfo,
  pageInfo => pageInfo.isFilterPopoverOpen,
);
export const getPriceFilterValues = createSelector(
  [getPriceFilter, getCurrencyRates],
  (priceFilter, currencyRates) => ({
    selectedMin: priceFilter.value.min,
    selectedMax: priceFilter.value.max,

    priceDistribution: priceFilter.priceDistribution,
    priceMax: priceFilter.priceMax,
    priceMin: priceFilter.priceMin,

    trimmedPriceDistribution: priceFilter.trimmedPriceDistribution,
    trimmedPriceMax: priceFilter.trimmedPriceMax,
    trimmedPriceMin: priceFilter.trimmedPriceMin,

    currency: priceFilter.currency,
    currencyRates,
  }),
);
export const getUniversities = createSelector(
  getSearch,
  search => search.universities,
);
export const getCitiesNearby = createSelector(
  getCitiesNearbyState,
  cities => cities.citiesNearby,
);
export const hasCitiesNearby = createSelector(
  getCitiesNearbyState,
  citiesNearby =>
    Boolean(citiesNearby.citiesNearby && citiesNearby.citiesNearby.length > 0),
);

export const getNumberOfListings = createSelector(
  getPageInfo,
  pageInfo => pageInfo.total,
);

export const getIsAdvertiserSearch = createSelector(
  getPageInfo,
  pageInfo => Boolean(pageInfo.userId),
);

export const getAdvertiserName = createSelector(
  getPageInfo,
  pageInfo => pageInfo.advertiserName,
);

const getLoadState = createSelector(
  getSearch,
  search => search.loadState,
);

export const getCityCanonical = createSelector(
  getLoadState,
  loadState => loadState.cityCanonical,
);
export const getCountryCanonical = createSelector(
  getLoadState,
  loadState => loadState.countryCanonical,
);
export const getCityLocalized = createSelector(
  getLoadState,
  loadState => loadState.cityLocalized,
);
export const getCountryLocalized = createSelector(
  getLoadState,
  loadState => loadState.countryLocalized,
);
export const getCitySearch = createSelector(
  getLoadState,
  loadState => loadState.citySearch,
);
export const getErrorState = createSelector(
  getLoadState,
  loadState => loadState.error,
);
export const isNotFound = createSelector(
  getLoadState,
  loadState => loadState.notFound,
);
export const getIsLoading = createSelector(
  getLoadState,
  loadState => loadState.loading,
);

export const getListingsSummary = createSelector(
  getSearch,
  search => search.listingsSummary,
);

export const isSuggestionBarVisible = createSelector(
  getSearch,
  isSuggestionBarVisibleSelector,
);

export const getAnalyticsForListings = createSelector(
  [getPageInfo, getListings, getListingsSummary],
  (pageInfo, listings, { organic, partner, isExhaustive }) => ({
    numberOfPartnerListings: partner,
    numberOfOrganicListings: organic,
    listingIdResults: listings.map(({ internalID }) => internalID),
    numberOfSearchResults: pageInfo.total,
    isNumberOfSearchResultsExhaustive: pageInfo.isExhaustive,
    areCountersOfOrganicAndPartnerListingsExhaustive: isExhaustive,
  }),
);

export const getLocation = createSelector(
  [getCityName, getCountryName],
  (city, country) => R.join(', ', [city, country]),
);

export const getDateFilterValues = createSelector(
  [getDateFilter],
  R.compose(
    R.map(formatDate),
    R.pick(['startDate', 'endDate']),
  ),
);

export const mapHasBounds = createSelector(
  getMap,
  map => !!map.bounds.topLat,
);

export const isMapAutoSearchEnabled = createSelector(
  getMap,
  map => map.isAutoSearchEnabled,
);

export const isMobileMapOpen = createSelector(
  getMap,
  map => map.isMobileMapOpen,
);

export const isMobileDatePickerOpen = createSelector(
  getPageInfo,
  pageInfo => pageInfo.isMobileDatePickerOpen,
);

export const isMapOutdated = createSelector(
  getMap,
  map => !map.isAutoSearchEnabled && map.isOutdated,
);

export const getMapParams = createSelector(
  [getMap, mapHasBounds],
  (map, hasBounds) => {
    const { uniID: uniId, location, bounds, latitude, longitude } = map;

    const mapLocation = uniId ? { uniId } : { location };

    return hasBounds
      ? {
          ...bounds,
          location,
          latitude,
          longitude,
        }
      : mapLocation;
  },
);

export const isSearchByPartner = createSelector(
  getMap,
  ({ uniID }) => !!uniID,
);

export const getCitiesNearbyFilter = createSelector(
  [getCitiesNearbyState, getCitySearch],
  ({ limit, minPopulation, minRooms }, citySearch) => ({
    citySearch,
    cityLimit: limit,
    cityMinPopulation: minPopulation,
    cityMinRooms: minRooms,
  }),
);

/* eslint-disable max-params */
export const getOtherFiltersCount = createSelector(
  [
    getCategoriesFilter,
    getFurnitureFilter,
    getBillsFilter,
    getRegistrationFilter,
    getRulesFilter,
    getRecentlyAddedFilter,
    getRoomsFilter,
    getPriceFilter,
    getGenderFilter,
    getSuitableForFilter,
    getMediaQueryBreakpoints,
  ],
  (
    kindsFilter,
    furnitureFilter,
    billsFilter,
    registrationFilter,
    rules,
    recentlyAddedFilter,
    roomsFilter,
    priceFilter,
    genderFilter,
    suitableForFilter,
    breakpoints,
  ) => {
    const categoriesCount = kindsFilter.length;
    const furnitureCount = furnitureFilter ? 1 : 0;
    const billsCount = billsFilter ? 1 : 0;
    const registrationCount = registrationFilter ? 1 : 0;
    const rulesCount = rules.length;
    const genderCount = genderFilter.length;
    const recentlyAddedCount = recentlyAddedFilter ? 1 : 0;
    const rooms = roomsFilter.bedroomCount > 0 ? 1 : 0;
    const suitableForCount = suitableForFilter.length;

    const desktopCount =
      categoriesCount +
      furnitureCount +
      billsCount +
      registrationCount +
      recentlyAddedCount +
      rulesCount +
      genderCount +
      suitableForCount +
      rooms;

    if (breakpoints.lg) {
      return desktopCount;
    }

    const { min, max } = priceFilter.value;
    const priceCount = R.is(Number, min) && R.is(Number, max) ? 1 : 0;

    return desktopCount + priceCount;
  },
);

export const getSearchParams = createSelector(
  [
    getPageInfo,
    getMapParams,
    getPriceFilter,
    getDateFilter,
    getCategoriesFilter,
    getFurnitureFilter,
    getSuitableForFilter,
    getBillsFilter,
    getRegistrationFilter,
    getRulesFilter,
    getRecentlyAddedFilter,
    getGenderFilter,
    getRoomsFilter,
    getCitiesNearbyFilter,
    getImagesSize,
    getCitySearch,
  ],
  (
    pageInfo,
    mapParams,
    priceFilter,
    dateFilter,
    kindsFilter,
    furnitureFilter,
    suitableForFilter,
    billsFilter,
    registrationFilter,
    rulesFilter,
    recentlyAddedFilter,
    genderFilter,
    roomsFilter,
    citiesNearbyFilter,
    imagesSize,
    citySearch,
  ) => {
    const filters = {
      priceMax: priceFilter.value.max,
      priceMin: priceFilter.value.min,

      categories: kindsFilter,
      furniture: furnitureFilter,
      bills: billsFilter,
      suitableFor: suitableForFilter,
      registration: registrationFilter,
      recentlyAdded: recentlyAddedFilter,
      rooms: roomsFilter,
      rules: rulesFilter,
      gender: genderFilter,

      startDate: dateFilter.startDate
        ? moment.utc(dateFilter.startDate).format()
        : null,
      endDate: dateFilter.endDate
        ? moment.utc(dateFilter.endDate).format()
        : null,
    };

    const reqParams = {
      ...mapParams,
      ...filters,
      ...citiesNearbyFilter,

      citySearch,
      imagesSize,

      currency: priceFilter.currency,

      offset: pageInfo.offset,
      limit: pageInfo.limit,
      advertiserId: pageInfo.userId,

      // Constants
      priceDistributionSteps: PriceDistributionSteps,
    };

    return R.pickBy(val => R.not(R.isNil(val)), reqParams);
  },
);
/* eslint-enable max-params */

export const getSearchAlertParams = createSelector(
  [getUserId, getCityCanonical, getCountryCanonical, getSearchParams],
  (userId, city, country, searchParams) => {
    const {
      startDate,
      endDate,
      currency,
      priceMin: minPrice,
      priceMax: maxPrice,
      categories,
    } = searchParams;

    const kinds = getKindsFromCategories(categories);

    return {
      userId,
      city,
      country,
      startDate,
      endDate,
      currency,
      minPrice,
      maxPrice,
      kinds,
    };
  },
);

export const getIsSameAlert = createSelector(
  [getSearchAlertParams, getCurrentSearchAlert],
  (currentSearchAlertParams, searchAlert) =>
    areSearchAlertsEqual(currentSearchAlertParams, searchAlert),
);

export const isSearchAlertDisabled = createSelector(
  [getIsErrorSearchAlert, getIsLoadingSearchAlert, getIsSameAlert],
  (isError, isLoading, isSame) => isError || isLoading || isSame,
);

export const getSearchCollectionFilters = createSelector(
  [getPriceFilter, getCategoriesFilter, getFurnitureFilter],
  (priceFilter, kindsFilter, furnitureFilter) => ({
    priceMax: priceFilter.value.max,
    priceMin: priceFilter.value.min,
    currency: priceFilter.currency,

    categories: kindsFilter,
    furniture: furnitureFilter,
  }),
);

export const getAnalyticsForFilters = createSelector(
  [getAnalyticsForListings, getSearchParams, getCurrencyRates, getPriceFilter],
  (listingsAnalytics, searchParams, currencyRates, { currency }) => {
    const currencyRate = currencyRates[currency];

    const normalizePrice = R.pipe(
      // eslint-disable-next-line no-underscore-dangle
      R.divide(R.__, 100),
      // eslint-disable-next-line no-underscore-dangle
      R.divide(R.__, currencyRate),
      Math.round,
    );
    const filters = R.pipe(
      R.pick(Object.keys(RequiredAnalyticsFilters)),
      R.evolve({
        priceMax: normalizePrice,
        priceMin: normalizePrice,
      }),
    )(searchParams);

    const filterTypes = Object.keys(
      R.pickBy(
        (val, key) => !R.equals(val, RequiredAnalyticsFilters[key]),
        filters,
      ),
    );

    if (!R.isEmpty(filters) || searchParams.topLat) {
      return {
        currency: searchParams.currency,
        ...listingsAnalytics,
        ...RequiredAnalyticsFilters,
        ...filters,
        filterTypes,
        ...(searchParams.topLat
          ? {
              mapBoundsTopLatitude: searchParams.topLat,
              mapBoundsBottomLatitude: searchParams.bottomLat,
              mapBoundsLeftLongitude: searchParams.leftLng,
              mapBoundsRightLongitude: searchParams.rightLng,
            }
          : {}),
      };
    }

    return {};
  },
);

export const getUniversity = createSelector(
  [getMap, getUniversities],
  ({ uniID }, universities) =>
    R.find(R.propEq('entityId', uniID))(universities) || null,
);

export const getPageName = createSelector(
  [getUniversity, getCollectionFilter],
  (university, category) => {
    if (university) {
      return university.name;
    }

    return {
      [TypeValue.APARTMENT]: 'Apartments',
      [TypeValue.PRIVATE_ROOM]: 'Private Rooms',
      [TypeValue.SHARED_ROOM]: 'Shared Rooms',
      [TypeValue.STUDIO]: 'Studios',
      '': 'All accommodation',
    }[category];
  },
);

export { default as getPagerProps } from './getPagerProps';

export const getMarkers = createSelector(
  getListings,
  items => groupRoomsByCoords(items),
);

export const getMapCenter = createSelector(
  getMap,
  map => ({ lat: map.latitude, lng: map.longitude }),
);
